import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HardDrives, MagnifyingGlass, CheckCircle, Warning, ArrowClockwise, ShieldCheck } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NASへ手でコピーしたファイルを「アプリに認識させる」カード。
 *
 * 移行ツールは元の保存先から読めることが前提なので、Emergent 側が使えなくなると
 * 1件も進まない。ファイルを自分でNASへコピー済みなら、同じパスに実体があることを
 * 確認して、DBの保存先だけを付け替えればそのまま読めるようになる。
 */
export const AdoptNasFilesCard = () => {
  const [counts, setCounts] = useState(null);
  const [scan, setScan] = useState(null);
  const [migrated, setMigrated] = useState(null);
  const [ready, setReady] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadCounts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/storage/by-backend`, { withCredentials: true });
      setCounts(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || '件数の取得に失敗しました'); }
  }, []);
  const checkReady = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/storage/offload-readiness`, { withCredentials: true });
      setReady(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'チェックに失敗しました'); }
  }, []);

  useEffect(() => { loadCounts(); checkReady(); }, [loadCounts, checkReady]);

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/settings/storage/rebind`,
        { app: 'all', target: 'nas', dry_run: dryRun }, { withCredentials: true });
      setScan(r.data);
      const { found, rebound, checked } = r.data.totals;
      if (dryRun) {
        toast.success(`確認しました — NASに実体があるのは ${found}件 / ${checked}件`);
      } else {
        toast.success(`${rebound}件をNAS参照に切り替えました`);
        loadCounts(); checkReady();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || '処理に失敗しました'); }
    finally { setBusy(false); }
  };

  // 全アプリまとめて「元のまま残して NAS にコピー」。終われば鍵を止めても開ける。
  const migrateAll = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/settings/storage/migrate-all`,
        { target: 'nas', set_backend: true, delete_source: false }, { withCredentials: true });
      setMigrated(r.data);
      toast.success(`${r.data.started.length}件のアプリでコピーを開始しました`);
      loadCounts(); checkReady();
    } catch (e) { toast.error(e?.response?.data?.detail || 'コピーを開始できませんでした'); }
    finally { setBusy(false); }
  };

  const em = counts?.totals?.emergent ?? 0;
  const nas = counts?.totals?.nas ?? 0;

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden"
      data-testid="storage-adopt-card">
      <div className="px-5 py-4 border-b border-zinc-200">
        <p className="text-sm font-bold text-zinc-800 flex items-center gap-1.5">
          <HardDrives size={16} weight="duotone" className="text-amber-600" />
          すべてのファイルをNASへ移す
        </p>
        <p className="mt-1 text-[11px] text-zinc-500 leading-relaxed">
          保存先の切り替えは「これから上がるファイル」にだけ効きます。既にあるファイルは
          <span className="font-semibold">実体がある場所をDBが覚えている</span>ため、古い保存先から
          読もうとして失敗します。ここで全アプリまとめてNASへコピーし、DBの参照先も切り替えます。
          自分で手動コピー済みの場合は「すでにNASにあるか確認」→「参照先の切り替え」だけで済みます。
        </p>
      </div>

      <div className="px-5 py-4 flex flex-wrap items-center gap-4 border-b border-zinc-100">
        <span className="text-xs text-zinc-500">現在の保存先内訳</span>
        <span className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs text-sky-800"
          data-testid="storage-count-emergent">Emergent クラウド <b>{em}</b>件</span>
        <span className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800"
          data-testid="storage-count-nas">Asustor NAS <b>{nas}</b>件</span>
        <Button variant="outline" size="sm" className="ml-auto h-8 text-xs" onClick={loadCounts}
          data-testid="storage-count-refresh"><ArrowClockwise size={13} className="mr-1" /> 再読込</Button>
      </div>

      <div className="px-5 py-4 flex flex-wrap items-center gap-2">
        <Button size="sm" className="h-9 text-xs bg-teal-700 hover:bg-teal-800" disabled={busy}
          onClick={migrateAll} data-testid="storage-migrate-all">
          <HardDrives size={14} className="mr-1" /> 全アプリの既存ファイルをNASへコピー
        </Button>
        <Button size="sm" variant="outline" className="h-9 text-xs" disabled={busy}
          onClick={() => run(true)} data-testid="storage-adopt-scan">
          <MagnifyingGlass size={14} className="mr-1" /> すでにNASにあるか確認（変更しません）
        </Button>
        <Button size="sm" variant="outline" className="h-9 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
          disabled={busy || !scan?.totals?.found}
          onClick={() => run(false)} data-testid="storage-adopt-apply">
          <CheckCircle size={14} className="mr-1" />
          {scan?.totals?.found ? `${scan.totals.found}件をNAS参照に切り替える` : '参照先の切り替え（先に確認してください）'}
        </Button>
        {busy && <span className="text-xs text-zinc-500">処理中…</span>}
      </div>
      <p className="px-5 pb-4 text-[11px] text-zinc-500">
        「コピー」は元のファイルを消しません。コピー後に<span className="font-semibold">整合性チェック</span>
        でエラーが 0 件になれば、Emergent 側の鍵を止めても全ファイルが開けます。
      </p>

      <div className={`px-5 py-4 border-t ${ready?.ready ? 'border-teal-200 bg-teal-50/60' : 'border-amber-200 bg-amber-50/60'}`}
        data-testid="storage-offload-readiness">
        <p className="flex items-center gap-1.5 text-xs font-bold">
          <ShieldCheck size={15} weight="duotone" className={ready?.ready ? 'text-teal-700' : 'text-amber-700'} />
          {ready === null ? 'Emergentの鍵を止めても平気か確認中…'
            : ready.ready
              ? 'Emergentの鍵を止めても業務に影響はありません（ファイルはすべてNASにあります）'
              : 'まだEmergentの鍵に依存しています — 止めるとエラーになります'}
          <button type="button" onClick={checkReady}
            className="ml-auto text-[11px] font-normal text-zinc-500 underline hover:text-zinc-700"
            data-testid="storage-readiness-recheck">再チェック</button>
        </p>
        {ready?.notes?.length > 0 && (
          <ul className="mt-2 space-y-1">
            {ready.notes.map((n) => (
              <li key={n} className="text-[11px] text-teal-800">・{n}</li>
            ))}
          </ul>
        )}
        {ready && !ready.ready && (
          <ul className="mt-2 space-y-1.5">
            {ready.blockers.map((b) => (
              <li key={b.kind} className="text-[11px] text-amber-900">
                <span className="font-semibold">・{b.detail}</span>
                <span className="block pl-3 text-amber-700">→ {b.action}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {migrated && (
        <div className="px-5 pb-4 text-xs" data-testid="storage-migrate-all-result">
          <p className="font-semibold text-teal-700">
            {migrated.started.length}件のアプリでコピーを開始しました（下の「移行履歴」で進捗が見られます）
          </p>
          <ul className="mt-1 space-y-0.5 text-[11px] text-zinc-600">
            {migrated.started.map((s) => (
              <li key={s.app}>● {s.app} — 対象 {s.pending}件</li>
            ))}
            {migrated.skipped.map((s) => (
              <li key={s.app} className="text-zinc-400">○ {s.app} — {s.reason}</li>
            ))}
          </ul>
        </div>
      )}

      {scan && (
        <div className="px-5 pb-5" data-testid="storage-adopt-result">
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="text-zinc-600">確認 <b>{scan.totals.checked}</b>件</span>
            <span className="text-teal-700">NASに実体あり <b>{scan.totals.found}</b>件</span>
            <span className="text-zinc-500">NASに無い <b>{scan.totals.missing}</b>件</span>
            {!scan.dry_run && <span className="text-amber-700 font-semibold">切り替え済み <b>{scan.totals.rebound}</b>件</span>}
          </div>
          {scan.totals.missing > 0 && (
            <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-800">
              <Warning size={13} className="mt-0.5 shrink-0" />
              NASに無いファイルは、元の保存先と同じパス（下のサンプル参照）でNASへコピーしてから、もう一度確認してください。
            </p>
          )}
          {scan.by_collection?.length > 0 && (
            <table className="mt-3 w-full text-[11px]">
              <thead className="text-zinc-500">
                <tr><th className="text-left py-1">アプリ / 保管場所</th><th className="text-right">確認</th><th className="text-right">NASにあり</th><th className="text-right">無し</th></tr>
              </thead>
              <tbody>
                {scan.by_collection.map((r) => (
                  <tr key={r.collection} className="border-t border-zinc-100">
                    <td className="py-1 text-zinc-700">{r.app} / <span className="font-mono text-zinc-400">{r.collection}</span></td>
                    <td className="text-right">{r.checked}</td>
                    <td className="text-right text-teal-700">{r.found}</td>
                    <td className="text-right text-zinc-400">{r.missing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {scan.samples?.length > 0 && (
            <details className="mt-3">
              <summary className="text-[11px] text-zinc-500 cursor-pointer">ファイルパスのサンプルを見る（NASへ置く場所の確認用）</summary>
              <ul className="mt-1 space-y-0.5">
                {scan.samples.map((s, i) => (
                  <li key={i} className="font-mono text-[10px] text-zinc-500 break-all">
                    <span className={s.found ? 'text-teal-600' : 'text-zinc-400'}>{s.found ? '● あり' : '○ 無し'}</span>{' '}
                    {s.path}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default AdoptNasFilesCard;
